import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Trash2, UserPlus } from "lucide-react";
import {
  CreateUserSchema,
  ROLE_LABELS,
  UpdateClinicConfigSchema,
  isClinician,
  type AvailabilityBlock,
  type CreateUserInput,
  type UpdateClinicConfigInput,
} from "@clinic/types";
import { api } from "@/lib/api";
import { useMe } from "@/lib/auth";
import { PageHeader } from "@/components/PageHeader";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { FieldError, Input, Label, Select } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function minutesToTime(min: number): string {
  return `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
}
function timeToMinutes(time: string): number {
  const [h = 0, m = 0] = time.split(":").map(Number);
  return h * 60 + m;
}

/* ================= Clinic profile ================= */

function ClinicTab() {
  const queryClient = useQueryClient();
  const clinic = useQuery({ queryKey: ["clinic"], queryFn: () => api.settings.getClinic() });
  const form = useForm<UpdateClinicConfigInput>({
    resolver: zodResolver(UpdateClinicConfigSchema),
    values: clinic.data
      ? {
          name: clinic.data.name,
          address: clinic.data.address,
          phone: clinic.data.phone,
          email: clinic.data.email,
        }
      : undefined,
  });
  const update = useMutation({
    mutationFn: (input: UpdateClinicConfigInput) => api.settings.updateClinic(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["clinic"] }),
  });

  return (
    <Card className="max-w-xl">
      <CardTitle>Clinic profile</CardTitle>
      <form onSubmit={form.handleSubmit((v) => update.mutate(v))} className="space-y-4">
        <div>
          <Label>Clinic name</Label>
          <Input {...form.register("name")} />
          <FieldError message={form.formState.errors.name?.message} />
        </div>
        <div>
          <Label>Address</Label>
          <Input {...form.register("address")} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Phone</Label>
            <Input {...form.register("phone")} />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" {...form.register("email")} />
          </div>
        </div>
        {update.isSuccess && <p className="text-sm text-success">Saved.</p>}
        <Button type="submit" loading={update.isPending}>
          Save clinic profile
        </Button>
      </form>
    </Card>
  );
}

/* ================= Availability ================= */

interface DayRow {
  enabled: boolean;
  start: string;
  end: string;
}

function AvailabilityTab() {
  const queryClient = useQueryClient();
  const availability = useQuery({
    queryKey: ["availability"],
    queryFn: () => api.settings.getAvailability(),
  });
  const [rows, setRows] = useState<DayRow[]>(
    DAYS.map(() => ({ enabled: false, start: "09:00", end: "17:00" })),
  );

  useEffect(() => {
    if (availability.data) {
      setRows(
        DAYS.map((_, day) => {
          const block = availability.data.find((b) => b.dayOfWeek === day);
          return block
            ? {
                enabled: true,
                start: minutesToTime(block.startMinute),
                end: minutesToTime(block.endMinute),
              }
            : { enabled: false, start: "09:00", end: "17:00" };
        }),
      );
    }
  }, [availability.data]);

  const save = useMutation({
    mutationFn: () => {
      const blocks: AvailabilityBlock[] = rows
        .map((row, day) => ({
          dayOfWeek: day,
          startMinute: timeToMinutes(row.start),
          endMinute: timeToMinutes(row.end),
          enabled: row.enabled,
        }))
        .filter((b) => b.enabled)
        .map(({ enabled: _e, ...b }) => b);
      return api.settings.setAvailability(blocks);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["availability"] }),
  });

  const setRow = (day: number, patch: Partial<DayRow>) =>
    setRows((prev) => prev.map((row, i) => (i === day ? { ...row, ...patch } : row)));

  return (
    <Card className="max-w-xl">
      <CardTitle>Weekly availability</CardTitle>
      <p className="mb-4 text-sm text-text-secondary">
        Shown as working hours on the calendar. Appointments outside these hours are still
        possible but highlighted.
      </p>
      <div className="space-y-2">
        {DAYS.map((dayName, day) => {
          const row = rows[day]!;
          return (
            <div
              key={dayName}
              className="flex items-center gap-4 rounded-lg border bg-bg px-4 py-2.5"
              style={{ borderColor: "var(--accent-subtle)" }}
            >
              <label className="flex w-32 cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={row.enabled}
                  onChange={(e) => setRow(day, { enabled: e.target.checked })}
                  className="accent-[var(--accent)]"
                />
                {dayName}
              </label>
              {row.enabled ? (
                <div className="flex items-center gap-2 text-sm">
                  <Input
                    type="time"
                    className="w-28"
                    value={row.start}
                    onChange={(e) => setRow(day, { start: e.target.value })}
                  />
                  <span className="text-text-tertiary">to</span>
                  <Input
                    type="time"
                    className="w-28"
                    value={row.end}
                    onChange={(e) => setRow(day, { end: e.target.value })}
                  />
                </div>
              ) : (
                <span className="text-sm text-text-tertiary">Closed</span>
              )}
            </div>
          );
        })}
      </div>
      {save.isError && (
        <p className="mt-3 text-sm text-danger">
          {save.error instanceof Error ? save.error.message : "Save failed"}
        </p>
      )}
      <Button className="mt-4" loading={save.isPending} onClick={() => save.mutate()}>
        Save availability
      </Button>
    </Card>
  );
}

/* ================= Users ================= */

function UsersTab({ meId }: { meId: string }) {
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const users = useQuery({ queryKey: ["users"], queryFn: () => api.users.list() });

  const form = useForm<CreateUserInput>({
    resolver: zodResolver(CreateUserSchema),
    defaultValues: { role: "RECEPTIONIST" },
  });
  const create = useMutation({
    mutationFn: (input: CreateUserInput) => api.users.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      setAddOpen(false);
      form.reset({ role: "RECEPTIONIST" });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.users.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
  });

  return (
    <Card className="max-w-xl">
      <div className="mb-4 flex items-center justify-between">
        <CardTitle className="mb-0">Staff accounts</CardTitle>
        <Button size="sm" onClick={() => setAddOpen(true)}>
          <UserPlus size={14} /> Add user
        </Button>
      </div>
      <div className="space-y-2">
        {(users.data ?? []).map((u) => (
          <div
            key={u.id}
            className="flex items-center gap-3 rounded-lg border bg-bg px-4 py-3"
            style={{ borderColor: "var(--accent-subtle)" }}
          >
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium">{u.name}</div>
              <div className="truncate text-xs text-text-tertiary">{u.email}</div>
            </div>
            <Badge tone={u.role === "RECEPTIONIST" ? "neutral" : "active"}>
              {ROLE_LABELS[u.role]}
            </Badge>
            {u.id !== meId && (
              <Button
                variant="ghost"
                size="icon"
                title="Delete user"
                onClick={() => {
                  if (confirm(`Delete ${u.name}'s account?`)) remove.mutate(u.id);
                }}
              >
                <Trash2 size={14} />
              </Button>
            )}
          </div>
        ))}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent title="Add staff member">
          <form onSubmit={form.handleSubmit((v) => create.mutate(v))} className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input {...form.register("name")} />
              <FieldError message={form.formState.errors.name?.message} />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" {...form.register("email")} />
              <FieldError message={form.formState.errors.email?.message} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Role</Label>
                <Select {...form.register("role")}>
                  <option value="RECEPTIONIST">Clinic Staff</option>
                  <option value="DOCTOR">Doctor</option>
                  <option value="ADMIN">Admin</option>
                </Select>
              </div>
              <div>
                <Label>Password</Label>
                <Input type="password" {...form.register("password")} />
                <FieldError message={form.formState.errors.password?.message} />
              </div>
            </div>
            {create.isError && (
              <p className="text-sm text-danger">
                {create.error instanceof Error ? create.error.message : "Failed to add user"}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setAddOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={create.isPending}>
                Add user
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ================= Email preview ================= */

const EMAIL_TEMPLATES = [
  { value: "confirmation", label: "Booking confirmation" },
  { value: "reminder24h", label: "24-hour reminder" },
  { value: "reminder1w", label: "1-week reminder" },
  { value: "welcome", label: "Welcome" },
  { value: "welcomeFollowup", label: "7-day booking nudge" },
  { value: "cancelled", label: "Cancellation confirmation" },
];

function EmailPreviewTab() {
  const [template, setTemplate] = useState("reminder24h");
  return (
    <Card>
      <div className="mb-4 flex items-center justify-between">
        <CardTitle className="mb-0">Email templates</CardTitle>
        <Select className="w-56" value={template} onChange={(e) => setTemplate(e.target.value)}>
          {EMAIL_TEMPLATES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </Select>
      </div>
      <iframe
        title="Email preview"
        src={api.settings.emailPreviewUrl(template)}
        className="h-[560px] w-full rounded-lg border bg-white"
        style={{ borderColor: "var(--accent-subtle)" }}
      />
    </Card>
  );
}

/* ================= Page ================= */

export function SettingsPage() {
  const me = useMe();
  // Admin inherits the doctor's management access.
  const isDoctor = me.data ? isClinician(me.data.role) : false;

  return (
    <div>
      <PageHeader title="Settings" />
      <Tabs defaultValue={isDoctor ? "clinic" : "appearance"}>
        <TabsList>
          {isDoctor && <TabsTrigger value="clinic">Clinic</TabsTrigger>}
          {isDoctor && <TabsTrigger value="availability">Availability</TabsTrigger>}
          {isDoctor && <TabsTrigger value="users">Users</TabsTrigger>}
          <TabsTrigger value="emails">Emails</TabsTrigger>
          <TabsTrigger value="appearance">Appearance</TabsTrigger>
        </TabsList>
        {isDoctor && (
          <TabsContent value="clinic">
            <ClinicTab />
          </TabsContent>
        )}
        {isDoctor && (
          <TabsContent value="availability">
            <AvailabilityTab />
          </TabsContent>
        )}
        {isDoctor && me.data && (
          <TabsContent value="users">
            <UsersTab meId={me.data.id} />
          </TabsContent>
        )}
        <TabsContent value="emails">
          <EmailPreviewTab />
        </TabsContent>
        <TabsContent value="appearance">
          <Card className="max-w-xl">
            <CardTitle>Appearance</CardTitle>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Theme</div>
                <div className="text-xs text-text-tertiary">
                  Dark mode first — light mode also fully supported.
                </div>
              </div>
              <ThemeToggle />
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
